import { useState, type FormEvent } from 'react';
import { motion } from 'motion/react';
import { X, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { trackEvent } from '../lib/posthog';
import { useTranslation } from 'react-i18next';

interface AuthGateProps {
  onClose: () => void;
  /** Optional: pre-fill email (e.g. from a previous input) */
  initialEmail?: string;
  /** Which mode to show first (default: 'signup') */
  initialMode?: 'signin' | 'signup';
  /** Allow toggling between signin/signup (default: true) */
  allowModeSwitch?: boolean;
  /** Called after a successful signup with the new user id */
  onSignUpSuccess?: (userId: string) => Promise<void> | void;
}

export function AuthGate({ onClose, initialEmail = '', initialMode = 'signup', allowModeSwitch = true, onSignUpSuccess }: AuthGateProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'signin' | 'signup'>(initialMode);
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (mode === 'signup') {
        const { data, error } = await supabase.auth.signUp({ email, password });
        if (error) throw error;
        if (data.user && onSignUpSuccess) await onSignUpSuccess(data.user.id);
        trackEvent('signup_completed');
        setSuccess(true);
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        trackEvent('signin_completed');
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t('common.error');
      trackEvent('auth_error', { mode, error_type: msg });
      if (msg.includes('Invalid login credentials')) {
        setError(t('auth.errors.invalidCredentials'));
      } else if (msg.includes('Email not confirmed')) {
        setError(t('auth.errors.emailNotConfirmed'));
      } else if (msg.includes('User already registered')) {
        setError(t('auth.errors.emailInUse'));
        setMode('signin');
      } else if (msg.includes('Password should be')) {
        setError(t('auth.errors.passwordTooShort'));
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm px-4 pb-6 sm:pb-0"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <motion.div
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 60, opacity: 0 }}
        transition={{ type: 'spring', bounce: 0.15, duration: 0.5 }}
        className="w-full max-w-sm bg-white rounded-[2.5rem] p-8 shadow-2xl"
      >
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-2xl font-extrabold tracking-tighter text-stone-900">
            {mode === 'signup' ? t('auth.createAccount') : t('auth.signIn')}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-stone-700 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {success ? (
          <div className="text-center py-4">
            <div className="text-5xl mb-4">✉️</div>
            <p className="font-bold text-stone-900 mb-2">{t('auth.checkEmail')}</p>
            <p className="text-sm text-stone-500">{t('auth.checkEmailDesc', { defaultValue: 'A confirmation link has been sent to' })} <strong>{email}</strong>.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-stone-500 uppercase tracking-widest">{t('auth.email')}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                placeholder={t('auth.emailPlaceholder')}
                className="w-full px-4 py-3.5 rounded-2xl bg-stone-50 border border-stone-200 text-stone-900 font-medium text-sm outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 transition-all"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-stone-500 uppercase tracking-widest">{t('auth.password')}</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                placeholder={t('auth.passwordPlaceholder')}
                className="w-full px-4 py-3.5 rounded-2xl bg-stone-50 border border-stone-200 text-stone-900 font-medium text-sm outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 transition-all"
              />
            </div>

            {error && (
              <p className="text-sm font-semibold text-red-500 bg-red-50 rounded-xl px-4 py-3">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-stone-900 text-white py-4 rounded-2xl font-bold text-sm shadow-lg hover:bg-stone-800 transition-all active:scale-95 disabled:opacity-60 flex items-center justify-center gap-2 mt-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {mode === 'signup' ? t('auth.createMyAccount') : t('auth.signIn')}
            </button>

            {allowModeSwitch && (
              <button
                type="button"
                onClick={() => { setMode(mode === 'signup' ? 'signin' : 'signup'); setError(null); }}
                className="text-sm font-semibold text-stone-500 hover:text-stone-900 transition-colors text-center"
              >
                {mode === 'signup'
                  ? t('auth.alreadyAccount')
                  : t('auth.noAccount')}
              </button>
            )}
          </form>
        )}
      </motion.div>
    </motion.div>
  );
}
